import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import buffaloWildWingsLogo from '../assets/Buffalo-Wild-Wings-Logo.png';
import arbysLogo from '../assets/Arbys_logo.png';
import moesLogo from '../assets/Moes_Logo_Diamond.png';
import sonicLogo from '../assets/SONIC_New_Logo_2020.png';
import auntieAnnesLogo from '../assets/Auntie-Annes®-on-white-1.png';
import AboutSection from '../components/AboutSection';

const monthlyPlans = [
  {
    name: 'Starter',
    price: 440,
    services: [
      { label: 'Graphic Design', included: true },
      { label: 'Illustrations', included: true },
      { label: 'Web & App UI Design', included: false },
      { label: 'Motion Graphics', included: false },
      { label: 'Video Editing', included: false },
      { label: 'Website Development', included: false },
    ],
    highlight: false,
  },
  {
    name: 'Pro',
    price: 590,
    services: [
      { label: 'Graphic Design', included: true },
      { label: 'Illustrations', included: true },
      { label: 'Web & App UI Design', included: true },
      { label: 'Motion Graphics', included: true },
      { label: 'Video Editing', included: true },
      { label: 'Website Development', included: false },
    ],
    highlight: false,
  },
  {
    name: 'Build',
    price: 1390,
    services: [
      { label: 'Graphic Design', included: true },
      { label: 'Illustrations', included: true },
      { label: 'Web & App UI Design', included: true },
      { label: 'Motion Graphics', included: true },
      { label: 'Video Editing', included: true },
      { label: 'Website Development', included: true },
    ],
    highlight: true,
  },
];

const annualPlans = [
  {
    name: 'Graphics',
    price: 440 * 12 * 0.9, // 10% off for annual
    services: [
      { label: 'Graphic Design', included: true },
      { label: 'Illustrations', included: true },
      { label: 'Web & App UI Design', included: false },
      { label: 'Motion Graphics', included: false },
      { label: 'Video Editing', included: false },
      { label: 'Website Development', included: false },
    ],
    highlight: false,
  },
  {
    name: 'Experience',
    price: 590 * 12 * 0.9, // 10% off for annual
    services: [
      { label: 'Graphic Design', included: true },
      { label: 'Illustrations', included: true },
      { label: 'Web & App UI Design', included: true },
      { label: 'Motion Graphics', included: true },
      { label: 'Video Editing', included: true },
      { label: 'Website Development', included: false },
    ],
    highlight: false,
  },
  {
    name: 'Build',
    price: 1390 * 12 * 0.9, // 10% off for annual
    services: [
      { label: 'Graphic Design', included: true },
      { label: 'Illustrations', included: true },
      { label: 'Web & App UI Design', included: true },
      { label: 'Motion Graphics', included: true },
      { label: 'Video Editing', included: true },
      { label: 'Website Development', included: true },
    ],
    highlight: true,
  },
];

const Pricing: React.FC = () => {
  const [pricingTab, setPricingTab] = useState<'monthly' | 'annual'>('monthly');
  const { user } = useAuth();

  const getCtaLink = () => {
    return user ? '/subscription' : '/signup';
  };

  const getCtaText = () => {
    return user ? 'Choose Plan' : 'Get Started Now';
  };

  return (
    <section className="py-16 bg-white min-h-screen">
      <div className="container mx-auto px-4">
        <div className="mb-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-2">Membership levels</h2>
          <p className="text-lg text-gray-700">Choose a plan that's right for you.</p>
        </div>
        <h1 className="text-4xl font-bold text-center mb-10">Pricing</h1>
        <div className="flex justify-center mb-8">
          <button
            className={`px-6 py-2 rounded-l font-semibold border-t border-l border-b ${pricingTab === 'monthly' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-800'}`}
            onClick={() => setPricingTab('monthly')}
          >
            Monthly
          </button>
          <button
            className={`px-6 py-2 rounded-r font-semibold border-t border-r border-b ${pricingTab === 'annual' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-800'}`}
            onClick={() => setPricingTab('annual')}
          >
            Annual
          </button>
        </div>
        {pricingTab === 'monthly' ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {monthlyPlans.map((plan, idx) => (
              <div
                key={plan.name}
                className={`rounded-3xl bg-[#f7fbff] shadow p-8 flex flex-col border transition-all duration-300 ${plan.highlight ? 'border-2 border-primary' : 'border border-gray-200'}`}
                style={{ minWidth: 0 }}
              >
                <h3 className="text-2xl font-bold mb-2 text-black font-sans">{plan.name}</h3>
                <div className="flex items-end mb-2">
                  <span className="text-5xl font-bold text-primary font-sans">${plan.price.toLocaleString()}</span>
                  <span className="text-gray-400 text-xl font-semibold ml-1">/month</span>
                </div>
                <div className="mb-4 text-base">
                  <span className="font-bold text-primary">1 Active Task</span> <span className="text-gray-700">at a time</span>
                </div>
                <hr className="my-4" />
                <div className="mb-2 font-bold text-lg">Services</div>
                <ul className="mb-4 space-y-2">
                  {plan.services.map((service, i) => (
                    <li key={service.label} className={`flex items-center text-base ${service.included ? 'text-black' : 'text-gray-400 line-through'}`}>
                      {service.included ? (
                        <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                      ) : (
                        <span className="material-icons mr-2 text-lg">cancel</span>
                      )}
                      {service.label}
                    </li>
                  ))}
                </ul>
                <hr className="my-4" />
                <div className="flex items-center text-base">
                  <span className="text-primary font-semibold mr-1">24–48 hrs</span>
                  <span className="text-gray-700">Delivery Time</span>
                  <span className="material-icons text-gray-400 ml-2 text-base" title="Most requests delivered in 1–2 business days">info</span>
                </div>
                <Link to={getCtaLink()} className="mt-6 bg-primary text-white px-6 py-3 rounded-lg font-semibold text-center hover:bg-primary/90 transition-colors">
                  {getCtaText()}
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {annualPlans.map((plan, idx) => (
              <div
                key={plan.name}
                className={`rounded-3xl bg-[#f7fbff] shadow p-8 flex flex-col border transition-all duration-300 ${plan.highlight ? 'border-2 border-primary' : 'border border-gray-200'}`}
                style={{ minWidth: 0 }}
              >
                <h3 className="text-2xl font-bold mb-2 text-black font-sans">{plan.name}</h3>
                <div className="flex items-end mb-2">
                  <span className="text-5xl font-bold text-primary font-sans">${plan.price.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  <span className="text-gray-400 text-xl font-semibold ml-1">/year</span>
                </div>
                <div className="mb-4 text-base">
                  <span className="font-bold text-primary">1 Active Task</span> <span className="text-gray-700">at a time</span>
                </div>
                <hr className="my-4" />
                <div className="mb-2 font-bold text-lg">Services</div>
                <ul className="mb-4 space-y-2">
                  {plan.services.map((service, i) => (
                    <li key={service.label} className={`flex items-center text-base ${service.included ? 'text-black' : 'text-gray-400 line-through'}`}>
                      {service.included ? (
                        <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                      ) : (
                        <span className="material-icons mr-2 text-lg">cancel</span>
                      )}
                      {service.label}
                    </li>
                  ))}
                </ul>
                <hr className="my-4" />
                <div className="flex items-center text-base">
                  <span className="text-primary font-semibold mr-1">24–48 hrs</span>
                  <span className="text-gray-700">Delivery Time</span>
                  <span className="material-icons text-gray-400 ml-2 text-base" title="Most requests delivered in 1–2 business days">info</span>
                </div>
                <Link to={getCtaLink()} className="mt-6 bg-primary text-white px-6 py-3 rounded-lg font-semibold text-center hover:bg-primary/90 transition-colors">
                  {getCtaText()}
                </Link>
              </div>
            ))}
          </div>
        )}
        
        {/* Brands Section */}
        <div className="py-12 bg-gray-50 mt-16">
          <div className="container mx-auto px-4">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              WE WORKED WITH GLOBAL LARGEST BRANDS
            </h3>
            <div className="flex justify-center items-center gap-12 flex-wrap">
              <img 
                src={buffaloWildWingsLogo} 
                alt="Buffalo Wild Wings" 
                className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300"
              />
              <img 
                src={arbysLogo} 
                alt="Arby's" 
                className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300"
              />
              <img 
                src={moesLogo} 
                alt="Moes's" 
                className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300"
              />
              <img 
                src={sonicLogo} 
                alt="Sonic" 
                className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300"
              />
              <img 
                src={auntieAnnesLogo} 
                alt="Auntie Annes" 
                className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>
          </div>
        </div>
        {/* About Section from App.tsx */}
        <AboutSection />
      </div>
    </section>
  );
};

export default Pricing; 